import { useCallback, useEffect, useState } from 'react';
import { Check, Users, X } from 'lucide-react';
import type { DiscoverySubmission } from '@spawnwire/shared';
import { Button, clsx, Spinner } from '../../components/ui';
import { errorMessage, http } from '../../lib/api';
import { CATEGORY_META } from '../../lib/discover';
import { formatDate } from '../../lib/moderation';
import { useStore } from '../../store/store';
import { ui } from '../../store/ui';
import { SpaceIcon } from '../shell/SpaceIcon';
import { Empty, PageHeader, ReasonDialog, Tabs, UserChip } from './adminUi';
import { loadAdminStats } from './store';

type Filter = 'pending' | 'approved' | 'denied' | 'all';

const STATUS_STYLE: Record<DiscoverySubmission['status'], string> = {
  pending: 'bg-warning text-black',
  approved: 'bg-success text-white',
  denied: 'bg-danger text-white',
  withdrawn: 'bg-active text-muted',
};

function SubmissionCard({ s, onDecide }: { s: DiscoverySubmission; onDecide: (decision: 'approve' | 'deny') => void }) {
  const meId = useStore((st) => st.me?.id);
  const isAdmin = useStore((st) => st.me?.staffRole === 'admin');
  const { icon: Icon, label, color } = CATEGORY_META[s.category];
  const own = s.submittedBy?.id === meId || s.space?.ownerId === meId;
  return (
    <li className="overflow-hidden rounded-xl border border-line bg-sidebar">
      <div className="relative h-24" style={{ background: `linear-gradient(135deg, ${color}, ${color}55)` }}>
        {s.space?.bannerUrl && <img src={s.space.bannerUrl} alt="" className="absolute inset-0 size-full object-cover" />}
        <span className={clsx('absolute right-3 top-3 rounded px-1.5 py-0.5 text-[11px] font-bold uppercase', STATUS_STYLE[s.status])}>{s.status}</span>
        <div className="absolute -bottom-6 left-4 rounded-[30%] border-4 border-sidebar bg-sidebar">
          <SpaceIcon name={s.space?.name ?? s.spaceName} iconUrl={s.space?.iconUrl ?? null} size={48} active />
        </div>
      </div>
      <div className="space-y-3 px-4 pb-4 pt-8">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <h3 className="text-base font-bold text-fg">{s.space?.name ?? s.spaceName}</h3>
          {s.space && s.space.name !== s.spaceName && <span className="text-xs text-faint">(submitted as “{s.spaceName}”)</span>}
          {!s.space && <span className="text-xs text-danger">Space deleted</span>}
          <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium text-white" style={{ background: color }}>
            <Icon className="size-3" aria-hidden /> {label}
          </span>
          {s.space && (
            <span className="flex items-center gap-1 text-xs text-muted">
              <Users className="size-3.5" aria-hidden /> {s.space.memberCount}
            </span>
          )}
        </div>
        <p className="whitespace-pre-wrap text-sm text-fg">{s.description || <span className="text-faint">No description.</span>}</p>
        <div className="flex flex-wrap items-center gap-4 text-xs text-muted">
          <span className="flex items-center gap-2">
            Submitted by <UserChip user={s.submittedBy} size={20} />
          </span>
          <span>{formatDate(s.createdAt)}</span>
          {s.space && <span className="font-mono text-faint">{s.spaceId}</span>}
        </div>
        {s.reviewer && (
          <div className="rounded-lg bg-main p-3 text-sm">
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
              {s.status === 'approved' ? 'Approved' : s.status === 'denied' ? 'Denied' : 'Reviewed'} by <UserChip user={s.reviewer} size={20} />
              {s.reviewedAt && <span>{formatDate(s.reviewedAt)}</span>}
            </div>
            {s.reviewNote && <p className="mt-1 text-fg">“{s.reviewNote}”</p>}
          </div>
        )}
        {s.status === 'pending' &&
          (!isAdmin ? (
            <p className="rounded-lg bg-main p-3 text-sm text-muted">Only admins can approve or deny Discover submissions.</p>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={() => onDecide('approve')}>
                <Check className="size-4" aria-hidden /> Approve
              </Button>
              <Button variant="danger" onClick={() => onDecide('deny')}>
                <X className="size-4" aria-hidden /> Deny
              </Button>
              {own && <span className="text-xs text-muted">Your own community. The staff audit log notes that you reviewed it yourself.</span>}
            </div>
          ))}
      </div>
    </li>
  );
}

/** Staff review of communities asking to be listed on Discover. */
export function DiscoveryPage() {
  const [filter, setFilter] = useState<Filter>('pending');
  const [rows, setRows] = useState<DiscoverySubmission[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deciding, setDeciding] = useState<{ s: DiscoverySubmission; decision: 'approve' | 'deny' } | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setRows(await http.get<DiscoverySubmission[]>(`/admin/discovery?status=${filter}`));
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [filter]);

  useEffect(() => {
    setRows(null);
    void load();
  }, [load]);

  return (
    <div>
      <PageHeader title="Discover review" description="Communities only appear on Discover after an admin approves them. The owner gets a DM from Spawnwire either way.">
        <Tabs<Filter>
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'pending', label: 'Waiting' },
            { value: 'approved', label: 'Approved' },
            { value: 'denied', label: 'Denied' },
            { value: 'all', label: 'All' },
          ]}
        />
      </PageHeader>
      {error && <p className="mb-3 text-sm text-danger">{error}</p>}
      {!rows ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <Empty>{filter === 'pending' ? 'Nothing waiting for review.' : 'Nothing here.'}</Empty>
      ) : (
        <ul className="grid gap-4 lg:grid-cols-2">
          {rows.map((s) => (
            <SubmissionCard key={s.id} s={s} onDecide={(decision) => setDeciding({ s, decision })} />
          ))}
        </ul>
      )}
      {deciding && (
        <ReasonDialog
          title={deciding.decision === 'approve' ? `Approve ${deciding.s.space?.name ?? deciding.s.spaceName}?` : `Deny ${deciding.s.space?.name ?? deciding.s.spaceName}?`}
          description={
            deciding.decision === 'approve'
              ? 'It will be listed on Discover straight away and the owner gets a DM.'
              : 'It stays off Discover. The owner gets a DM with your reason and can apply again.'
          }
          label={deciding.decision === 'approve' ? 'Note to the owner (optional)' : 'Reason (sent to the owner)'}
          placeholder={deciding.decision === 'approve' ? 'Welcome aboard!' : 'Add a description of your community and its rules.'}
          confirmLabel={deciding.decision === 'approve' ? 'Approve' : 'Deny'}
          danger={deciding.decision === 'deny'}
          optional={deciding.decision === 'approve'}
          onClose={() => setDeciding(null)}
          onSubmit={async (note) => {
            await http.post(`/admin/discovery/${deciding.s.id}`, { decision: deciding.decision, note });
            ui.toast(deciding.decision === 'approve' ? 'Approved. It is now on Discover.' : 'Denied. The owner has been told why.', 'success');
            void load();
            void loadAdminStats();
          }}
        />
      )}
    </div>
  );
}
